import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

export interface LeaveEventResult {
  ok: boolean;
  /** An accepted membership was removed. */
  left: boolean;
  /** A still-pending application was withdrawn. */
  applicationWithdrawn: boolean;
  /** Message to surface when `ok` is false. */
  error?: string;
}

/**
 * Remove the signed-in volunteer from an event.
 *
 * Everything happens in `public.leave_event` (SECURITY DEFINER, see
 * supabase/migrations/20260907120000_volunteer_leave_event.sql): the pending application is
 * withdrawn, the `volunteers` row is deactivated, task assignments are pruned, the legacy
 * chat participant row is dropped and the organizer is notified — in one transaction.
 * A volunteer has no RLS grant to prune their own task assignments, so this cannot be done
 * from the client.
 */
export async function leaveEvent(eventId: string): Promise<LeaveEventResult> {
  const { data, error } = await supabase.rpc('leave_event', { p_event_id: eventId });

  if (error) {
    logger.error('leave_event failed', { eventId, error });
    return {
      ok: false,
      left: false,
      applicationWithdrawn: false,
      error: error.message || 'Failed to leave the event. Please try again.',
    };
  }

  const row = (data ?? {}) as { left?: boolean; application_withdrawn?: boolean };
  return {
    ok: true,
    left: row.left === true,
    applicationWithdrawn: row.application_withdrawn === true,
  };
}
